import { useAuthStore } from '@/stores/authStore';

const BASE_URL = import.meta.env.VITE_HASMCP_ACCOUNTS_API_BASE_URL;

/**
 * A wrapper around fetch that handles:
 * 1. Base URL
 * 2. Authorization Headers (from Pinia)
 * 3. JSON stringifying/parsing
 * 4. Error throwing
 * 5. Automatic Token Refresh on 401
 */
async function client(endpoint, { body, ...customConfig } = {}) {
  const authStore = useAuthStore()

  const headers = { 'Content-Type': 'application/json' }

  // 1. Inject Token if it exists
  if (authStore.token) {
    headers.Authorization = `Bearer ${authStore.token}`
  } else {
    return Promise.reject({ message: 'token not found' })
  }

  const config = {
    method: body ? 'POST' : 'GET',
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  };

  if (body) {
    config.body = JSON.stringify(body)
  }

  // 2. Initial Request
  let response = await fetch(`${BASE_URL}${endpoint}`, config)

  // 3. Handle 401 (Unauthorized) with Retry Logic
  if (response.status === 401 && !customConfig._retry) {
    // Attempt to refresh the token
    const refreshed = await authStore.refreshAccessToken();

    if (refreshed) {
      // Retry the original request with the new token
      // Pass _retry flag to prevent infinite loops
      return client(endpoint, { body, ...customConfig, _retry: true })
    } else {
      // Refresh failed -> Logout
      authStore.signout();
      return Promise.reject({ message: 'Unauthorized' })
    }
  }

  // 4. Parse JSON safely
  const data = await response.json().catch(() => ({}))

  // 5. Handle Errors
  if (response.ok) {
    return { data, status: response.status }
  } else {
    const error = new Error(data.message || response.statusText)
    error.response = { data, status: response.status }
    return Promise.reject(error)
  }
}

export const api = {
  get: (endpoint, config) => client(endpoint, { ...config, method: 'GET' }),
  post: (endpoint, body, config) => client(endpoint, { ...config, body, method: 'POST' }),
  put: (endpoint, body, config) => client(endpoint, { ...config, body, method: 'PUT' }),
  patch: (endpoint, body, config) => client(endpoint, { ...config, body, method: 'PATCH' }),
  delete: (endpoint, config) => client(endpoint, { ...config, method: 'DELETE' }),
};

// Raw client for endpoints that are NOT under the /api/v1 base path (e.g. /oauth2/...)
const RAW_BASE_URL = import.meta.env.VITE_HASMCP_ACCOUNTS_API_ROOT_URL;

async function rawClient(endpoint, { body, headers: extraHeaders, ...customConfig } = {}) {
  const authStore = useAuthStore()
  const headers = { 'Content-Type': 'application/json', ...extraHeaders }
  if (authStore.token) {
    headers.Authorization = `Bearer ${authStore.token}`
  }
  const config = {
    method: body ? 'POST' : 'GET',
    ...customConfig,
    headers,
  }
  if (body) config.body = JSON.stringify(body)

  const response = await fetch(`${RAW_BASE_URL}${endpoint}`, config)
  const data = await response.json().catch(() => ({}))
  if (response.ok) return { data, status: response.status }
  const error = new Error(data?.error?.message || response.statusText)
  error.response = { data, status: response.status }
  throw error
}

export const rawApi = {
  get: (endpoint, { params, ...config } = {}) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return rawClient(endpoint + qs, { ...config, method: 'GET' })
  },
  post: (endpoint, body, config) => rawClient(endpoint, { ...config, body, method: 'POST' }),
};